import type { MemberOut, TeamOut } from "@sagot/api-client";

type Bag = Record<string, unknown>;

export type RoutingTarget = { kind: "team"; name: string } | { kind: "person"; name: string } | { kind: "fallback" };

export interface AgentRouting {
  target: RoutingTarget;
  /** Team that gets payment questions (only when the agent's payment trigger is on). */
  payment: string | null;
  /** A team whose routing topics match the conversation gets it before the default target. */
  matchTopics: boolean;
}

function text(bag: Bag, key: string): string | null {
  const value = bag[key];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Where one agent's handoffs really go (mirrors the server's `routing_view`): the chosen team or
 * person when it still exists and can be reached, otherwise the Owner and Admins.
 */
export function describeRouting(escalation: Bag, teams: readonly TeamOut[], members: readonly MemberOut[]): AgentRouting {
  const teamName = (id: string | null) => teams.find((team) => team.id === id)?.name ?? null;
  const person = members.find((m) => m.user_id === text(escalation, "default_assignee_user_id") && m.status === "active");
  const team = teamName(text(escalation, "default_team_id"));
  let target: RoutingTarget = { kind: "fallback" };
  if (escalation.route_to === "person" && person) target = { kind: "person", name: person.name };
  else if (escalation.route_to !== "person" && team) target = { kind: "team", name: team };
  return {
    target,
    payment: escalation.payment_topics === true ? teamName(text(escalation, "payment_team_id")) : null,
    matchTopics: escalation.match_team_topics !== false,
  };
}

export interface TeamTraits {
  roundRobin: boolean;
  lead: string | null;
  email: string | null;
  topics: string[];
}

/** What a team row shows besides its name: how handoffs are shared, who leads, where mail goes. */
export function teamTraits(team: TeamOut, members: readonly MemberOut[]): TeamTraits {
  const lead = members.find((m) => m.id === team.lead_membership_id);
  return { roundRobin: team.round_robin, lead: lead?.name ?? null, email: team.notify_email ?? null, topics: team.routing_topics };
}
